/**
 * Floating "back to top" button. Appears after the buyer scrolls past a
 * threshold (default 480px) and smooth-scrolls window to the top on
 * click. Used on `/shop/inventory` where the card grid scrolls long,
 * but the component is route-agnostic — drop it in any long-scroll
 * shop page and it lights up automatically.
 *
 * Listens to `scroll` with passive: true so it doesn't interfere with
 * the browser's scroll perf optimizations. Cleans up on unmount.
 *
 * Positioned bottom-right with `safe-area-inset-bottom` padding so it
 * doesn't sit underneath the iOS home indicator. z-40 keeps it under
 * the CardDetailModal (z-50) but above everything else.
 */
import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

interface BackToTopProps {
  /** Pixels of vertical scroll before the button appears. Default 480. */
  threshold?: number;
}

export function BackToTop({ threshold = 480 }: BackToTopProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  const scrollToTop = () => {
    // `prefers-reduced-motion` is respected automatically by `behavior:
    // "smooth"` in evergreen browsers.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
          initial={{ opacity: 0, scale: 0.8, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 12 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="fixed bottom-4 right-4 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur-md transition-colors hover:bg-primary/20"
          style={{
            background: "rgba(201,168,76,0.18)",
            borderColor: "rgba(201,168,76,0.45)",
            color: "#f0c96e",
            paddingBottom: "env(safe-area-inset-bottom)",
            marginBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <ArrowUp size={18} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
